import { Languages, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { LANGUAGE_LABELS } from '@hetivo/hetivo-lib-front-utils';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { SidebarUserCard } from '../sidebar-user-card';

export interface SidebarUserMenuProps {
  currentLanguage: string;
  displayName?: string;
  email?: string;
  languages: string[];
  onLanguageChange: (lang: string) => void;
  onSignOut: () => void;
}

export function SidebarUserMenu({
  currentLanguage,
  displayName = '',
  email = '',
  languages,
  onLanguageChange,
  onSignOut,
}: SidebarUserMenuProps): React.JSX.Element {
  const { t } = useTranslation();
  const ariaLabel = displayName ? `Open user menu for ${displayName}` : 'Open user menu';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarUserCard
          aria-label={ariaLabel}
          avatarClassName="bg-blue-500"
          fallbackInitials="??"
          name={displayName}
          subtitle={email}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56" side="top">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages className="me-2 size-4" />
            {t('auth.language')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={currentLanguage} onValueChange={onLanguageChange}>
              {languages.map((lang) => (
                <DropdownMenuRadioItem key={lang} value={lang}>
                  {LANGUAGE_LABELS[lang] ?? lang}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={(): void => void onSignOut()}>
          <LogOut className="me-2 size-4" />
          {t('auth.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
